/**
 * Quick Schedule Step
 *
 * Simplified scheduling step for existing customers requesting a swap or pickup.
 * Since we already know their address and container, this focuses only on "When?"
 *
 * Key Features:
 * - Pre-filled context (address, container info from existing order)
 * - ASAP button for quick next-available selection
 * - Calendar picker for specific date selection
 * - Order type awareness (swap vs pickup)
 */

import { useFormContext } from 'react-hook-form';
import { useState, useEffect, useMemo } from 'react';
import { addDays } from 'date-fns';
import { Loader2Icon, AlertCircleIcon } from 'lucide-react';
import { AvailabilityCalendar, type DayAvailability } from '../../../components/order/AvailabilityCalendar';
import { QuickSummary } from '../../../components/order/QuickSummary';
import { Button } from '../../../components/ui/button';
import { useProviderConfig } from '../../../contexts/ProviderConfigContext';
import type { OrderForm } from '../../../schemas/order.schema';

// Mock availability service - reusing same logic as ScheduleFirstStep
const mockGetAvailability = async (
  _providerId: string,
  startDate: Date,
  endDate: Date
): Promise<Map<string, DayAvailability>> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));

  const availability = new Map<string, DayAvailability>();
  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    const dayOfWeek = currentDate.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    let status: 'available' | 'limited' | 'unavailable' = 'available';
    let containers = 10;

    if (isWeekend) {
      status = 'limited';
      containers = 3;
    }

    if (!isWeekend && Math.random() < 0.1) {
      status = 'unavailable';
      containers = 0;
    }

    if (!isWeekend && status === 'available' && Math.random() < 0.2) {
      status = 'limited';
      containers = Math.floor(Math.random() * 3) + 1;
    }

    const dateKey = currentDate.toISOString().split('T')[0] as string;
    availability.set(dateKey, {
      date: new Date(currentDate),
      status,
      containers,
      slots: status === 'available' ? 8 : status === 'limited' ? 3 : 0,
    });

    currentDate = addDays(currentDate, 1);
  }

  return availability;
};

/**
 * Get next available date from availability map
 */
const getNextAvailableDate = (availability: Map<string, DayAvailability>): Date | null => {
  const dates = Array.from(availability.values())
    .filter(d => d.status === 'available' || d.status === 'limited')
    .sort((a, b) => a.date.getTime() - b.date.getTime());
  return dates[0]?.date || null;
};

export function QuickScheduleStep() {
  const { register, setValue, watch, formState: { errors } } = useFormContext<OrderForm>();
  const { config } = useProviderConfig();

  const [availability, setAvailability] = useState<Map<string, DayAvailability>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Watch form values
  const orderType = watch('orderType') as 'swap' | 'pickup';
  const containerSize = watch('containerSize');
  const serviceAddress = {
    street: watch('street') || '',
    city: watch('city') || '',
    state: watch('state') || 'CA',
    zipCode: watch('zipCode') || '',
    country: watch('country') || 'US',
  };
  const selectedDateStr = watch('deliveryDate');
  const selectedDate = selectedDateStr && typeof selectedDateStr === 'string'
    ? new Date(selectedDateStr)
    : null;

  // Date boundaries - shorter window for swap/pickup
  const minDate = useMemo(() => new Date(), []); // Today
  const maxDate = useMemo(() => addDays(new Date(), 14), []); // 2 weeks out

  // Next available date for ASAP button
  const nextAvailableDate = useMemo(() => {
    return getNextAvailableDate(availability);
  }, [availability]);

  // Fetch availability on mount
  useEffect(() => {
    const fetchAvailability = async () => {
      if (!config) return;

      setIsLoading(true);
      setError(null);

      try {
        const data = await mockGetAvailability(
          config.companyName || 'provider',
          minDate,
          maxDate
        );
        setAvailability(data);
      } catch (err) {
        setError('Unable to load availability. Please try again.');
        console.error('Failed to fetch availability:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAvailability();
  }, [config, minDate, maxDate]);

  const handleASAP = () => {
    if (!nextAvailableDate) return;

    const formattedDate = nextAvailableDate.toISOString().split('T')[0] ||
      nextAvailableDate.toISOString().substring(0, 10);
    setValue('deliveryDate', formattedDate);
    setValue('deliveryNotes', `ASAP - ${orderType} requested`);
  };

  const handleDateSelect = (date: Date) => {
    const formattedDate = date.toISOString().split('T')[0] ||
      date.toISOString().substring(0, 10);
    setValue('deliveryDate', formattedDate);
  };

  // Order type labels
  const orderTypeLabels = {
    swap: 'swap',
    pickup: 'pick up',
  };

  return (
    <div className="space-y-6">
      {/* Order Context Summary */}
      <QuickSummary
        orderType={orderType}
        containerSize={containerSize || ''}
        address={serviceAddress}
      />

      {/* Title - changes based on order type */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          When should we {orderTypeLabels[orderType]} your container?
        </h2>
        <p className="text-sm text-gray-600">
          {orderType === 'swap'
            ? "We'll swap your container with an empty one on your selected date."
            : "We'll pick up your container on your selected date."}
        </p>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2Icon className="w-8 h-8 animate-spin text-primary" />
          <span className="ml-2 text-gray-600">Checking availability...</span>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-start gap-3">
          <AlertCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Main Content */}
      {!isLoading && !error && (
        <>
          {/* ASAP Button */}
          {nextAvailableDate && (
            <Button
              type="button"
              onClick={handleASAP}
              size="lg"
              className="w-full bg-primary-500 text-white hover:bg-primary-600 h-auto py-4"
            >
              <div className="flex flex-col items-center gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-lg">⚡</span>
                  <span className="font-semibold">ASAP - Next Available</span>
                </div>
                <span className="text-sm opacity-90">
                  {nextAvailableDate.toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
              </div>
            </Button>
          )}

          {/* OR Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500 font-medium">OR</span>
            </div>
          </div>

          {/* Calendar */}
          <AvailabilityCalendar
            minDate={minDate}
            maxDate={maxDate}
            selectedDate={selectedDate}
            availability={availability}
            onDateSelect={handleDateSelect}
            showASAP={false} // We have our own ASAP button above
            isLoading={isLoading}
          />

          {/* Hidden input for form validation */}
          <input
            type="hidden"
            {...register('deliveryDate')}
          />

          {errors.deliveryDate && (
            <p className="text-sm text-red-600" role="alert">
              {String(errors.deliveryDate.message || 'Please select a date')}
            </p>
          )}

          {/* Additional Notes */}
          <div>
            <label
              htmlFor="additionalNotes"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Additional Notes <span className="text-gray-500 font-normal">(optional)</span>
            </label>
            <textarea
              id="additionalNotes"
              {...register('deliveryNotes')}
              className="w-full min-h-[80px] p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              placeholder="Any special instructions for the driver? (e.g., Container is behind garage)"
            />
          </div>

          {/* Service Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex items-start gap-3">
              <AlertCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-900 mb-2">Service Information</p>
                <ul className="text-blue-800 list-disc pl-5 space-y-1">
                  <li>
                    {orderType === 'swap'
                      ? 'We typically swap containers between 7 AM and 5 PM'
                      : 'Pickups are typically made between 7 AM and 5 PM'}
                  </li>
                  <li>We&apos;ll call you the day before to confirm timing</li>
                  <li>Someone doesn&apos;t need to be present for service</li>
                  {orderType === 'swap' && (
                    <li>Please ensure the container is accessible and not blocked</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
